import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Shield, Monitor, Building2, Menu, KeyRound, ImagePlus, Rewind } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { istTimeStr, istDateStr } from "@/lib/time";

function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) { setCurrent(""); setNext(""); setConfirmPw(""); } }, [open]);
  const submit = async () => {
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirmPw) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed. Please use the new password next time.");
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><KeyRound size={16} className="text-cyan-400" /> Change Password</DialogTitle>
          <DialogDescription className="mono text-[10px] text-zinc-500">Enter your current password and choose a new one. Minimum 6 characters.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Current password</Label>
            <Input data-testid="cp-current" type="password" className="mono bg-black border-zinc-800 rounded-none mt-1" value={current} onChange={(e) => setCurrent(e.target.value)} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">New password</Label>
            <Input data-testid="cp-new" type="password" className="mono bg-black border-zinc-800 rounded-none mt-1" value={next} onChange={(e) => setNext(e.target.value)} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Confirm new password</Label>
            <Input data-testid="cp-confirm" type="password" className="mono bg-black border-zinc-800 rounded-none mt-1" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="cp-submit" disabled={busy || !current || !next || !confirmPw} onClick={submit} className="rounded-none bg-cyan-500 hover:bg-cyan-400 text-black uppercase tracking-widest text-xs">
            {busy ? "Saving..." : "Update password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NavButtons({ user, active, onNavigate, inDrawer = false }) {
  const isSuper = user?.role === "super_admin";
  const isAdmin = user?.role === "admin";
  const size = inDrawer ? "w-full justify-start h-10" : "h-8 px-3";
  return (
    <>
      {isSuper && (
        <Button
          data-testid={inDrawer ? "drawer-nav-super" : "nav-super"}
          variant="ghost"
          onClick={() => onNavigate("/super")}
          className={`rounded-none ${size} text-[11px] uppercase tracking-widest ${active === "super" ? "bg-zinc-900 text-cyan-400" : "text-zinc-400 hover:text-zinc-100"}`}
        >
          <Building2 size={13} className="mr-1.5" /> Tenants
        </Button>
      )}
      {!isSuper && (
        <Button
          data-testid={inDrawer ? "drawer-nav-dashboard" : "nav-dashboard"}
          variant="ghost"
          onClick={() => onNavigate("/dashboard")}
          className={`rounded-none ${size} text-[11px] uppercase tracking-widest ${active === "dashboard" ? "bg-zinc-900 text-cyan-400" : "text-zinc-400 hover:text-zinc-100"}`}
        >
          <Monitor size={13} className="mr-1.5" /> Live View
        </Button>
      )}
      {!isSuper && (
        <Button
          data-testid={inDrawer ? "drawer-nav-gallery" : "nav-gallery"}
          variant="ghost"
          onClick={() => onNavigate("/gallery")}
          className={`rounded-none ${size} text-[11px] uppercase tracking-widest ${active === "gallery" ? "bg-zinc-900 text-cyan-400" : "text-zinc-400 hover:text-zinc-100"}`}
        >
          <ImagePlus size={13} className="mr-1.5" /> Gallery
        </Button>
      )}
      {!isSuper && (
        <Button
          data-testid={inDrawer ? "drawer-nav-playback" : "nav-playback"}
          variant="ghost"
          onClick={() => onNavigate("/playback")}
          className={`rounded-none ${size} text-[11px] uppercase tracking-widest ${active === "playback" ? "bg-zinc-900 text-amber-400" : "text-zinc-400 hover:text-zinc-100"}`}
        >
          <Rewind size={13} className="mr-1.5" /> Playback
        </Button>
      )}
      {(isAdmin || isSuper) && (
        <Button
          data-testid={inDrawer ? "drawer-nav-admin" : "nav-admin"}
          variant="ghost"
          onClick={() => onNavigate("/admin")}
          className={`rounded-none ${size} text-[11px] uppercase tracking-widest ${active === "admin" ? "bg-zinc-900 text-cyan-400" : "text-zinc-400 hover:text-zinc-100"}`}
        >
          <Shield size={13} className="mr-1.5" /> {isSuper ? "Manage" : "Admin"}
        </Button>
      )}
    </>
  );
}

export default function AppHeader({ active = "dashboard" }) {
  const { user, server, logout } = useAuth();
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const timeStr = istTimeStr(now);
  const dateStr = istDateStr(now);

  const onLogout = () => {
    setDrawerOpen(false);
    logout();
    navigate("/login");
  };
  const go = (path) => { setDrawerOpen(false); navigate(path); };

  return (
    <header className="border-b border-zinc-900 bg-black">
      <div className="flex items-center justify-between px-3 md:px-4 h-12 gap-2">
        {/* Left: Logo + desktop nav */}
        <div className="flex items-center gap-3 md:gap-6 min-w-0">
          <Logo size={26} className="shrink-0" />
          <nav className="hidden md:flex items-center gap-1">
            <NavButtons user={user} active={active} onNavigate={go} />
          </nav>
        </div>

        {/* Right: Desktop info OR Mobile hamburger */}
        <div className="hidden md:flex items-center gap-4">
          {server && (
            <div className="mono text-[10px] text-zinc-500">
              <span className="text-emerald-500">●</span> {server.ip}:{server.port}
            </div>
          )}
          <div className="mono text-[11px] text-zinc-300">
            <span className="text-zinc-500 mr-2">{dateStr}</span>
            <span className="text-cyan-300">{timeStr}</span>
            <span className="text-amber-400 text-[9px] ml-1">IST</span>
          </div>
          {user && (
            <div className="flex items-center gap-2 border-l border-zinc-900 pl-4">
              <span className="mono text-[11px] text-zinc-300">{user.username}</span>
              <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-zinc-800 text-zinc-500">
                {user.role.replace("_", " ")}
              </span>
              <Button
                data-testid="btn-change-password"
                variant="ghost"
                onClick={() => setChangePwOpen(true)}
                className="rounded-none h-7 px-2 text-zinc-400 hover:text-cyan-400"
                title="Change password"
              >
                <KeyRound size={13} />
              </Button>
              <Button
                data-testid="btn-logout"
                variant="ghost"
                onClick={onLogout}
                className="rounded-none h-7 px-2 text-zinc-400 hover:text-red-400"
              >
                <LogOut size={13} />
              </Button>
            </div>
          )}
        </div>

        {/* Mobile: time + hamburger */}
        <div className="flex md:hidden items-center gap-2">
          <div className="mono text-[10px] text-cyan-300 leading-tight text-right">
            <div>{timeStr}</div>
            <div className="text-amber-400 text-[8px]">IST</div>
          </div>
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <Button
                data-testid="mobile-menu-btn"
                variant="ghost"
                className="rounded-none h-9 w-9 p-0 text-zinc-300 border border-zinc-800"
              >
                <Menu size={18} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="rounded-none bg-zinc-950 border-zinc-800 text-zinc-100 p-0 w-72" data-testid="mobile-menu-drawer">
              <SheetTitle className="sr-only">Main menu</SheetTitle>
              <SheetDescription className="sr-only">Navigation, user info and sign out</SheetDescription>
              <div className="flex flex-col h-full">
                <div className="p-4 border-b border-zinc-900 flex items-center gap-3">
                  <Logo size={26} />
                </div>

                {user && (
                  <div className="p-4 border-b border-zinc-900">
                    <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Signed in as</div>
                    <div className="mono text-sm text-zinc-100 mt-1">{user.username}</div>
                    <span className="inline-block mt-1 mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-zinc-800 text-zinc-400">
                      {user.role.replace("_", " ")}
                    </span>
                    {server && (
                      <div className="mono text-[10px] text-zinc-500 mt-2">
                        <span className="text-emerald-500">●</span> {server.ip}:{server.port}
                      </div>
                    )}
                    <div className="mono text-[10px] text-zinc-500 mt-1">
                      {dateStr} {timeStr} <span className="text-amber-400">IST</span>
                    </div>
                  </div>
                )}

                <div className="p-2 border-b border-zinc-900 flex flex-col">
                  <NavButtons user={user} active={active} onNavigate={go} inDrawer />
                </div>

                <div className="p-2 mt-auto border-t border-zinc-900 space-y-1">
                  <Button
                    data-testid="mobile-change-password"
                    variant="ghost"
                    onClick={() => { setDrawerOpen(false); setChangePwOpen(true); }}
                    className="rounded-none w-full justify-start h-11 text-cyan-400 hover:bg-cyan-950/30 text-[11px] uppercase tracking-widest"
                  >
                    <KeyRound size={14} className="mr-2" /> Change password
                  </Button>
                  <Button
                    data-testid="mobile-logout"
                    variant="ghost"
                    onClick={onLogout}
                    className="rounded-none w-full justify-start h-11 text-red-400 hover:bg-red-950/40 text-[11px] uppercase tracking-widest"
                  >
                    <LogOut size={14} className="mr-2" /> Sign out
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
    </header>
  );
}
